import React, { useCallback, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesUpDown } from '@fortawesome/pro-light-svg-icons/faAnglesUpDown';
import { TypedMemo } from '../../../common/types';
import { MobileAppHandoffModal } from '../../../mobileApps/MobileAppHandoffModal';
import { PlanStatusVariant } from '../../hooks/usePlanStatus';
import { useAccountIdentity } from '../account/useAccountIdentity';
import { AccountArt } from '../account/AccountArt';
import { AccountWorkspaceName } from '../account/AccountWorkspaceName';
import { accountMenuMessages as messages } from '../account/accountMenuMessages';
import { smlNavigationChromeMessages as chrome } from '../smlNavigationMessages';
import { placeRailTip } from '../SidebarNavItem';
import { SidebarAccountMenu } from './SidebarAccountMenu';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole sidebar; each part uses its own slice of it */
import styles from '../../SMLSideBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** design s3: the colour of the plan dot. */
const planClass: Record<PlanStatusVariant, string | undefined> = {
  free: styles.planFree,
  trial: styles.planTrial,
  paid: styles.planPaid,
  lapsed: styles.planLapsed,
};

interface SidebarAccountCardProps {
  /** The social accounts that need a new sign-in: the red pip and the Connections count. */
  accountAttention: number;
}

/**
 * design: navigation-v2 1a, 1e, 1f. The account card at the bottom of the sidebar: the artist and
 * the plan (1 own artist), or the workspace and "{plan} · {n} artists" (2 or more). A red pip
 * means a social account needs a new sign-in. A click opens the account menu (6a). On the rail the
 * card is the art alone.
 */
const SidebarAccountCardComponent: React.FC<SidebarAccountCardProps> = ({ accountAttention }) => {
  const intl = useIntl();
  const identity = useAccountIdentity();
  const [isOpen, setOpen] = useState(false);
  const [isAppOpen, setAppOpen] = useState(false);
  const cardRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const hasAttention = accountAttention > 0;

  const toggle = useCallback(() => setOpen((open) => !open), []);
  const close = useCallback(() => setOpen(false), []);
  const openMobileApp = useCallback(() => {
    setOpen(false);
    setAppOpen(true);
  }, []);
  const closeMobileApp = useCallback(() => setAppOpen(false), []);
  const attentionText = hasAttention ? intl.formatMessage(chrome.channelsReconnect, { count: accountAttention }) : '';

  return (
    <div className={styles.accountSlot} onMouseEnter={placeRailTip} onFocus={placeRailTip}>
      {isOpen && (
        <SidebarAccountMenu
          id={menuId}
          email={identity.email}
          accountAttention={accountAttention}
          anchorRef={cardRef}
          onClose={close}
          onOpenMobileApp={openMobileApp}
        />
      )}
      <button
        ref={cardRef}
        type="button"
        className={clsx(styles.accountCard, isOpen && styles.accountCardOpen)}
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        data-testid="sidebar-account-card"
      >
        <AccountArt name={identity.name} avatarUrl={identity.avatarUrl} hasAttention={hasAttention} />
        <span className={styles.accountText}>
          <b className={styles.accountName}>
            {identity.isWorkspace ? <AccountWorkspaceName email={identity.email} /> : identity.name}
          </b>
          <span
            className={clsx(styles.accountPlan, planClass[identity.planVariant])}
            data-testid={`account-card-plan-${identity.planVariant}`}
          >
            <span className={styles.planDot} aria-hidden="true" />
            {identity.planLine}
          </span>
          {hasAttention && <span className="visually-hidden">{attentionText}</span>}
        </span>
        <FontAwesomeIcon icon={faAnglesUpDown} className={styles.accountChevron} />
        <span className={styles.tip} aria-hidden="true">
          {intl.formatMessage(messages.menu)}
        </span>
      </button>
      <MobileAppHandoffModal isOpen={isAppOpen} onClose={closeMobileApp} />
    </div>
  );
};

export const SidebarAccountCard = TypedMemo(SidebarAccountCardComponent);
